/**
 * TrainerLabPage — 7-day lab-usage timeline for admins.
 *
 * Layout:
 *  - 7 day tabs (Today · Tue 14 Jul · Wed 15 … · Mon 20 Jul)
 *  - Per tab: a Gantt-style timeline per centre/lab (rows), with hour ticks
 *    (9AM → 9PM) and each batch rendered as a positioned coloured block.
 *  - Filter chips: mode (All / Offline / Online), trainer search, centre.
 *  - Summary strip: offline count, online count, clashes, peak hour.
 *  - Untimed / bad-timing batches surface below the timeline as chips.
 *
 * Why a timeline (Gantt) and not a table?
 *   Batches have highly varied start-times (28 distinct values), so a
 *   fixed-column table would be inaccurate and a dynamic-column table
 *   shifts between tabs. A timeline is precise, day-comparable, and
 *   overlaps (physical double-bookings) pop visually.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  DoorOpen, Users, Clock, AlertTriangle, TrendingUp, Search,
  Wifi, Building2, RefreshCw, Info,
} from 'lucide-react';

// ─── Time helpers ──────────────────────────────────────────
// The timeline canvas spans DAY_START_MIN..DAY_END_MIN.
const DAY_START_MIN = 8 * 60;   // 8:00 AM
const DAY_END_MIN = 21 * 60;    // 9:00 PM
const DAY_TOTAL_MIN = DAY_END_MIN - DAY_START_MIN;

const HOURS = Array.from({ length: 14 }, (_, i) => 8 + i); // 8..21

function hourLabel(h) {
  if (h === 0) return '12 AM';
  if (h === 12) return '12 PM';
  return h < 12 ? `${h} AM` : `${h - 12} PM`;
}

function fmt12h(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function toDate(iso) {
  return new Date(iso + 'T00:00:00');
}

function daysAgoOrIn(iso, now) {
  const d = toDate(iso);
  const nowD = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diff = Math.round((d - nowD) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  return d.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short' });
}

// ─── Colour by trainer for consistent visual identity ──────
// Uses a deterministic hash of the trainer_name → indexed palette.
const TRAINER_COLORS = [
  { bg: 'bg-violet-500', ring: 'ring-violet-200', text: 'text-violet-900' },
  { bg: 'bg-emerald-500', ring: 'ring-emerald-200', text: 'text-emerald-900' },
  { bg: 'bg-amber-500', ring: 'ring-amber-200', text: 'text-amber-900' },
  { bg: 'bg-rose-500', ring: 'ring-rose-200', text: 'text-rose-900' },
  { bg: 'bg-sky-500', ring: 'ring-sky-200', text: 'text-sky-900' },
  { bg: 'bg-fuchsia-500', ring: 'ring-fuchsia-200', text: 'text-fuchsia-900' },
  { bg: 'bg-indigo-500', ring: 'ring-indigo-200', text: 'text-indigo-900' },
  { bg: 'bg-teal-500', ring: 'ring-teal-200', text: 'text-teal-900' },
  { bg: 'bg-orange-500', ring: 'ring-orange-200', text: 'text-orange-900' },
];

function trainerColor(name) {
  if (!name) return TRAINER_COLORS[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return TRAINER_COLORS[h % TRAINER_COLORS.length];
}

// ─── Overlap swim-lanes ────────────────────────────────────
// Given a list of batches with start_min/end_min, assign each to a lane
// index so overlapping bars stack vertically without visual collision.
function assignLanes(batches) {
  const laneEnd = []; // laneEnd[i] = end_min of last batch placed in lane i
  const withLane = [];
  const sorted = [...batches].sort((a, b) => a.start_min - b.start_min || a.end_min - b.end_min);
  for (const b of sorted) {
    let lane = laneEnd.findIndex((e) => e <= b.start_min);
    if (lane === -1) {
      lane = laneEnd.length;
      laneEnd.push(0);
    }
    laneEnd[lane] = b.end_min;
    withLane.push({ ...b, lane });
  }
  return { batches: withLane, laneCount: Math.max(1, laneEnd.length) };
}

// ─── Timeline bar ──────────────────────────────────────────
function TimelineBar({ batch, laneCount }) {
  const c = trainerColor(batch.trainer_name);
  const startPct = ((batch.start_min - DAY_START_MIN) / DAY_TOTAL_MIN) * 100;
  const widthPct = ((batch.end_min - batch.start_min) / DAY_TOTAL_MIN) * 100;
  const laneHeightPx = 34;
  const topPx = 4 + batch.lane * (laneHeightPx + 4);
  const isOffline = batch.training_mode === 'Offline';
  return (
    <div
      className={`absolute rounded-md px-2 py-1 shadow-sm text-white text-[10px] leading-tight overflow-hidden cursor-pointer hover:ring-2 ${c.bg} ${c.ring} ${!isOffline ? 'opacity-70' : ''}`}
      style={{
        left: `${Math.max(0, startPct)}%`,
        width: `${Math.max(2, widthPct)}%`,
        top: `${topPx}px`,
        height: `${laneHeightPx}px`,
      }}
      title={`#${batch.batch_number} · ${batch.trainer_name}
${batch.course_name || ''}
${fmt12h(batch.start_time_24h)} → ${fmt12h(batch.end_time_24h)}
${batch.training_mode} · ${batch.student_count || 0} students
Batch ends: ${batch.end_date || '—'}`}
      data-testid={`lab-batch-${batch.batch_id}`}
    >
      <div className="flex items-center gap-1 truncate">
        <span className="font-bold">#{batch.batch_number}</span>
        <span className="truncate">{(batch.trainer_name || '').split('.')[0]}</span>
        {!isOffline && <Wifi className="w-2.5 h-2.5 ml-auto flex-shrink-0" />}
      </div>
      <div className="text-[9px] opacity-90 truncate">
        {fmt12h(batch.start_time_24h)}–{fmt12h(batch.end_time_24h)}
      </div>
    </div>
  );
}

// ─── Timeline (per lab) ────────────────────────────────────
function LabTimeline({ lab, batches, isUnallocated = false }) {
  const { batches: withLane, laneCount } = useMemo(() => assignLanes(batches), [batches]);
  const heightPx = 4 + Math.max(1, laneCount) * 38;
  return (
    <div className={`relative border-b border-slate-100 ${isUnallocated ? 'bg-rose-50/30' : ''}`} data-testid={`lab-row-${lab.lab_id || 'unallocated'}`}>
      <div className="flex items-stretch">
        {/* Lab label */}
        <div className={`w-40 flex-shrink-0 flex flex-col justify-center px-3 py-2 border-r ${isUnallocated ? 'bg-rose-100/60 border-rose-200' : 'bg-slate-50/80 border-slate-200'}`}>
          <div className="flex items-center gap-1.5">
            {isUnallocated ? <AlertTriangle className="w-3.5 h-3.5 text-rose-600" /> : <Building2 className="w-3.5 h-3.5 text-slate-500" />}
            <p className={`text-xs font-semibold truncate ${isUnallocated ? 'text-rose-800' : 'text-slate-800'}`}>{lab.lab_number}</p>
          </div>
          <p className="text-[10px] text-slate-500 mt-0.5 truncate">{lab.location_name}{lab.capacity ? ` · cap ${lab.capacity}` : ''}</p>
          <p className="text-[10px] text-slate-500 mt-0.5">{batches.length} batch{batches.length === 1 ? '' : 'es'}</p>
        </div>
        {/* Timeline canvas */}
        <div className="relative flex-1" style={{ height: `${heightPx}px` }}>
          {/* Hour gridlines */}
          {HOURS.slice(0, -1).map((h) => (
            <div
              key={h}
              className="absolute top-0 bottom-0 border-l border-dashed border-slate-100"
              style={{ left: `${((h - HOURS[0]) / (HOURS[HOURS.length - 1] - HOURS[0])) * 100}%` }}
            />
          ))}
          {/* Bars */}
          {withLane.map((b) => (
            <TimelineBar key={b.batch_id} batch={b} laneCount={laneCount} />
          ))}
          {batches.length === 0 && (
            <p className="absolute inset-0 flex items-center justify-center text-[10px] text-slate-300 italic">
              {isUnallocated ? 'All batches have a lab assigned' : 'Lab free all day'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Time axis (hour ticks header) ─────────────────────────
function TimeAxis() {
  return (
    <div className="flex items-stretch bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
      <div className="w-40 flex-shrink-0 px-3 py-1.5 border-r border-slate-200 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Lab</div>
      <div className="relative flex-1 h-7">
        {HOURS.map((h, i) => (
          <div
            key={h}
            className="absolute top-0 h-full border-l border-slate-200 text-[10px] text-slate-500 pl-1 pt-1 tabular-nums"
            style={{ left: `${(i / (HOURS.length - 1)) * 100}%` }}
          >
            {hourLabel(h)}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Day summary strip ─────────────────────────────────────
function DaySummary({ day, onClickUnallocated }) {
  const { totals, peak_hour } = day;
  return (
    <div className="flex flex-wrap items-center gap-2 mb-3">
      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[11px]">
        <DoorOpen className="w-3 h-3 mr-1" /> {totals.offline} offline
      </Badge>
      <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200 text-[11px]">
        <Wifi className="w-3 h-3 mr-1" /> {totals.online} online
      </Badge>
      {totals.unallocated > 0 ? (
        <button
          onClick={onClickUnallocated}
          className="inline-flex items-center rounded-md border border-rose-300 bg-rose-100 text-rose-800 text-[11px] font-semibold px-2 py-0.5 hover:bg-rose-200 transition-colors"
          data-testid="day-unallocated-pill"
          title="Click to jump to unallocated batches"
        >
          <AlertTriangle className="w-3 h-3 mr-1" /> {totals.unallocated} unallocated
          {totals.unallocated_offline > 0 && <span className="ml-1 text-[10px] font-normal opacity-80">({totals.unallocated_offline} offline)</span>}
        </button>
      ) : (
        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[11px]">All labs assigned</Badge>
      )}
      {totals.clashes > 0 ? (
        <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-[11px]" data-testid="day-clash-badge">
          <AlertTriangle className="w-3 h-3 mr-1" /> {totals.clashes} clash{totals.clashes === 1 ? '' : 'es'}
        </Badge>
      ) : (
        <Badge variant="outline" className="bg-slate-50 text-slate-500 border-slate-200 text-[11px]">No clashes</Badge>
      )}
      {peak_hour && (
        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[11px]">
          <TrendingUp className="w-3 h-3 mr-1" /> Peak {hourLabel(peak_hour.hour_24)} · {peak_hour.concurrent} concurrent
        </Badge>
      )}
    </div>
  );
}

// ─── Untimed / bad-timing rollup ───────────────────────────
function UntimedList({ day }) {
  const all = [
    ...(day.labs || []).flatMap((l) => l.untimed),
    ...((day.unallocated?.untimed) || []),
  ];
  if (all.length === 0) return null;
  return (
    <Card className="p-3 mt-3 border-amber-200 bg-amber-50/40" data-testid="lab-untimed">
      <p className="text-[11px] font-semibold text-amber-800 mb-1.5 flex items-center gap-1">
        <Info className="w-3 h-3" /> Untimed / unparseable batches ({all.length})
      </p>
      <div className="flex flex-wrap gap-1.5">
        {all.map((b) => (
          <div key={b.batch_id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-amber-200 bg-white text-[10px]">
            <span className="font-mono font-semibold text-amber-800">#{b.batch_number}</span>
            <span className="text-slate-700 truncate max-w-[120px]">{b.trainer_name}</span>
            <span className="text-slate-400">·</span>
            <span className="text-slate-500 italic">{b.start_time_raw || '?'}–{b.end_time_raw || '?'}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ─── Unallocated batches card ──────────────────────────────
// Sits above the timeline so managers immediately see how many batches
// still need a lab assignment. Clicking a row opens Batch Management
// pre-filtered to that batch for quick assignment.
function UnallocatedCard({ unallocated, dayTotals }) {
  const all = [...(unallocated?.batches || []), ...(unallocated?.untimed || [])];
  const offlineCount = dayTotals?.unallocated_offline || 0;
  if (all.length === 0) return null;
  return (
    <Card className="p-4 mb-3 border-rose-200 bg-rose-50/40" data-testid="unallocated-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-bold text-rose-800 flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4" /> {all.length} batch{all.length === 1 ? '' : 'es'} without a lab
            {offlineCount > 0 && <span className="ml-1 text-[11px] font-medium bg-rose-200 text-rose-900 px-1.5 py-0.5 rounded">{offlineCount} offline (needs physical room)</span>}
          </p>
          <p className="text-[11px] text-rose-600 mt-1">Assign labs from <a className="underline font-semibold" href="/training/batches" target="_blank" rel="noreferrer">Batch Management</a> → the Lab column dropdown on each row.</p>
        </div>
      </div>
      <div className="mt-3 grid gap-1.5 max-h-[220px] overflow-y-auto pr-1">
        {all.map((b) => (
          <a
            key={b.batch_id}
            href={`/training/batches?focus=${encodeURIComponent(b.batch_id)}`}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 bg-white border border-rose-100 rounded px-2 py-1.5 text-[11px] hover:border-rose-300 hover:bg-rose-50/40 transition-colors"
            data-testid={`unalloc-batch-${b.batch_id}`}
          >
            <span className="font-mono font-bold text-rose-700">#{b.batch_number}</span>
            <span className="font-medium text-slate-800 truncate max-w-[200px]">{b.trainer_name || '—'}</span>
            <span className="text-slate-400 truncate max-w-[180px]">· {b.course_name || 'Course?'}</span>
            <span className="ml-auto flex items-center gap-1.5 text-[10px]">
              <span className={`px-1.5 py-0.5 rounded ${b.training_mode === 'Offline' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-sky-50 text-sky-700 border border-sky-200'}`}>{b.training_mode || '?'}</span>
              <span className="text-slate-500">{fmt12h(b.start_time_24h)}–{fmt12h(b.end_time_24h)}</span>
              <span className="text-slate-400">· {b.centre_name}</span>
              <span className="text-slate-400 hidden md:inline">→ Assign ↗</span>
            </span>
          </a>
        ))}
      </div>
    </Card>
  );
}

// ─── Transposed Grid View (columns = labs, rows = time slots) ─────
// Alternative to the Gantt timeline: 30-minute row buckets, one column per
// lab. Each cell shows the batches occupying that lab at that slot.
// Requested by users who prefer a matrix layout for scanning "who's in Lab 3
// at 11 AM" at a glance.
const SLOT_MIN = 30;
function LabGridView({ day, unallocated }) {
  const labs = day.labs || [];
  const cols = [
    ...labs.map((l) => ({ id: l.lab_id, label: l.lab_number, sub: l.location_name, batches: l.batches, isUnalloc: false })),
    ...((unallocated?.batches?.length || 0) > 0 ? [{ id: '__unalloc__', label: 'Unallocated', sub: `${unallocated.batches.length} batches`, batches: unallocated.batches, isUnalloc: true }] : []),
  ];
  const slots = [];
  for (let m = DAY_START_MIN; m < DAY_END_MIN; m += SLOT_MIN) {
    slots.push(m);
  }
  const cellFor = (col, slotStart) => {
    const slotEnd = slotStart + SLOT_MIN;
    return col.batches.filter((b) => b.start_min < slotEnd && b.end_min > slotStart);
  };
  return (
    <div className="overflow-x-auto" data-testid="lab-grid-view">
      <table className="min-w-full border-collapse text-[11px]">
        <thead>
          <tr className="bg-slate-100 sticky top-0 z-10">
            <th className="w-20 text-left px-2 py-1.5 border border-slate-200 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Time</th>
            {cols.map((c) => (
              <th
                key={c.id}
                className={`text-left px-2 py-1.5 border border-slate-200 min-w-[140px] ${c.isUnalloc ? 'bg-rose-100' : ''}`}
              >
                <div className="text-[11px] font-semibold text-slate-800 flex items-center gap-1">
                  {c.isUnalloc && <AlertTriangle className="w-3 h-3 text-rose-600" />}
                  {c.label}
                </div>
                <div className="text-[9px] text-slate-500">{c.sub}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {slots.map((s) => {
            const h = Math.floor(s / 60);
            const mm = s % 60;
            const hh = h === 0 ? 12 : h > 12 ? h - 12 : h;
            const ampm = h >= 12 ? 'PM' : 'AM';
            const rowLabel = `${hh}:${String(mm).padStart(2, '0')} ${ampm}`;
            const isHourStart = mm === 0;
            return (
              <tr key={s} className={isHourStart ? 'bg-slate-50/70' : ''}>
                <td className={`px-2 py-0.5 border border-slate-100 text-[10px] tabular-nums ${isHourStart ? 'font-semibold text-slate-700' : 'text-slate-400'}`}>
                  {rowLabel}
                </td>
                {cols.map((c) => {
                  const cellBatches = cellFor(c, s);
                  const isClash = cellBatches.filter((b) => b.training_mode === 'Offline').length >= 2;
                  return (
                    <td
                      key={c.id}
                      className={`border border-slate-100 align-top px-1 py-0.5 ${isClash ? 'bg-rose-50' : c.isUnalloc && cellBatches.length ? 'bg-rose-50/40' : ''}`}
                      data-testid={`grid-cell-${c.id}-${s}`}
                    >
                      <div className="flex flex-col gap-0.5">
                        {cellBatches.map((b) => {
                          const col = trainerColor(b.trainer_name);
                          return (
                            <div
                              key={b.batch_id}
                              className={`px-1.5 py-0.5 rounded text-white text-[9px] leading-tight truncate cursor-pointer ${col.bg} ${b.training_mode !== 'Offline' ? 'opacity-70' : ''}`}
                              title={`#${b.batch_number} · ${b.trainer_name}\n${b.course_name || ''}\n${fmt12h(b.start_time_24h)} → ${fmt12h(b.end_time_24h)}\n${b.training_mode} · ${b.student_count || 0} students`}
                            >
                              <span className="font-bold">#{b.batch_number}</span>{' '}
                              <span className="truncate">{(b.trainer_name || '').split('.')[0]}</span>
                              {b.training_mode !== 'Offline' && <Wifi className="w-2 h-2 inline ml-0.5" />}
                            </div>
                          );
                        })}
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function TrainerLabPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeDay, setActiveDay] = useState(null); // iso date
  const [modeFilter, setModeFilter] = useState('offline'); // 'offline' | 'online' | 'all'
  const [locationFilter, setLocationFilter] = useState('all');
  const [trainerSearch, setTrainerSearch] = useState('');
  const [viewMode, setViewMode] = useState('gantt'); // 'gantt' | 'grid'

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/training/trainer-lab/week');
      setData(r.data);
      // Default active day = today (first day) — only on initial load
      setActiveDay((cur) => cur || r.data?.days?.[0]?.date || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load Trainer Lab');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Apply mode / centre / trainer filters to a day payload
  const filterDay = useCallback((day) => {
    if (!day) return day;
    const matchBatch = (b) => {
      if (modeFilter !== 'all' && (b.training_mode || '').toLowerCase() !== modeFilter) return false;
      if (trainerSearch && !(b.trainer_name || '').toLowerCase().includes(trainerSearch.toLowerCase())) return false;
      return true;
    };
    const labs = day.labs
      .filter((lab) => locationFilter === 'all' || lab.location_name === locationFilter)
      .map((lab) => ({
        ...lab,
        batches: lab.batches.filter(matchBatch),
        untimed: lab.untimed.filter(matchBatch),
      }));
    const unallocated = day.unallocated ? {
      ...day.unallocated,
      batches: day.unallocated.batches.filter(matchBatch),
      untimed: day.unallocated.untimed.filter(matchBatch),
    } : { batches: [], untimed: [] };

    let offline = 0, online = 0;
    for (const l of labs) {
      for (const b of l.batches) {
        if (b.training_mode === 'Offline') offline++;
        else if (b.training_mode === 'Online') online++;
      }
    }
    for (const b of unallocated.batches.concat(unallocated.untimed)) {
      if (b.training_mode === 'Offline') offline++;
      else if (b.training_mode === 'Online') online++;
    }
    const unalloc_total = unallocated.batches.length + unallocated.untimed.length;
    const unalloc_offline = unallocated.batches.concat(unallocated.untimed).filter(b => b.training_mode === 'Offline').length;
    return {
      ...day, labs, unallocated,
      totals: { ...day.totals, offline, online, unallocated: unalloc_total, unallocated_offline: unalloc_offline },
    };
  }, [modeFilter, locationFilter, trainerSearch]);

  const currentDay = useMemo(() => {
    if (!data?.days) return null;
    const raw = data.days.find((d) => d.date === activeDay) || data.days[0];
    return filterDay(raw);
  }, [data, activeDay, filterDay]);

  const scrollToUnallocated = () => {
    document.querySelector('[data-testid="unallocated-card"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const now = new Date();

  return (
    <div className="p-6 space-y-4" data-testid="trainer-lab-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <DoorOpen className="w-6 h-6 text-violet-600" /> Trainer Lab
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            7-day physical lab occupancy inferred from Running batches. A batch releases the lab once its end date passes.
            <span className="ml-1 italic">Offline batches consume physical labs; Online are shown ghosted for context.</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden bg-white" data-testid="lab-view-toggle">
            <button
              onClick={() => setViewMode('gantt')}
              className={`px-2.5 py-1 text-[11px] font-medium transition-colors ${viewMode === 'gantt' ? 'bg-violet-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
              data-testid="lab-view-gantt"
              title="Gantt: rows are labs, x-axis is time"
            >
              Timeline
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`px-2.5 py-1 text-[11px] font-medium border-l border-slate-200 transition-colors ${viewMode === 'grid' ? 'bg-violet-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
              data-testid="lab-view-grid"
              title="Grid: columns are labs, rows are 30-min time slots"
            >
              Grid
            </button>
          </div>
          <Button size="sm" variant="outline" className="h-8" onClick={load} disabled={loading} data-testid="lab-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Filter row */}
      <Card className="p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mr-1">Mode</span>
            {['offline', 'all', 'online'].map((m) => (
              <button
                key={m}
                onClick={() => setModeFilter(m)}
                className={`px-2 py-1 rounded-md text-[10px] font-medium border transition-colors ${modeFilter === m ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                data-testid={`lab-mode-${m}`}
              >
                {m === 'all' ? 'All modes' : m.charAt(0).toUpperCase() + m.slice(1)}
              </button>
            ))}
          </div>

          {(data?.locations?.length || 0) > 0 && (
            <div className="flex items-center gap-1">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mr-1 ml-3">Location</span>
              <button
                onClick={() => setLocationFilter('all')}
                className={`px-2 py-1 rounded-md text-[10px] font-medium border transition-colors ${locationFilter === 'all' ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-slate-600 border-slate-200'}`}
              >
                All
              </button>
              {data.locations.map((c) => (
                <button
                  key={c}
                  onClick={() => setLocationFilter(c)}
                  className={`px-2 py-1 rounded-md text-[10px] font-medium border transition-colors ${locationFilter === c ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-slate-600 border-slate-200'}`}
                  data-testid={`lab-location-${c}`}
                >
                  {c}
                </button>
              ))}
            </div>
          )}

          <div className="relative flex-1 min-w-[200px] max-w-[280px] ml-auto">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
            <Input
              value={trainerSearch}
              onChange={(e) => setTrainerSearch(e.target.value)}
              placeholder="Search trainer…"
              className="h-8 pl-7 text-xs"
              data-testid="lab-trainer-search"
            />
          </div>
        </div>
      </Card>

      {/* Day tabs */}
      {data && (
        <Tabs value={activeDay || ''} onValueChange={setActiveDay}>
          <TabsList className="w-full justify-start overflow-x-auto flex-wrap h-auto bg-slate-100 p-1">
            {data.days.map((d) => {
              const label = daysAgoOrIn(d.date, now);
              const hasClash = d.totals.clashes > 0;
              return (
                <TabsTrigger
                  key={d.date}
                  value={d.date}
                  className="data-[state=active]:bg-white data-[state=active]:shadow flex-col gap-0 h-auto py-1.5 px-3"
                  data-testid={`lab-day-tab-${d.date}`}
                >
                  <div className="text-[11px] font-semibold flex items-center gap-1">
                    {label}
                    {hasClash && <span className="w-1.5 h-1.5 rounded-full bg-rose-500" title={`${d.totals.clashes} clashes`} />}
                  </div>
                  <div className="text-[9px] text-slate-500 tabular-nums">{d.dow} · {d.date.slice(5)}</div>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {data.days.map((d) => (
            <TabsContent key={d.date} value={d.date} className="mt-3">
              {currentDay && currentDay.date === d.date && (
                <>
                  <DaySummary day={currentDay} onClickUnallocated={scrollToUnallocated} />

                  <UnallocatedCard unallocated={currentDay.unallocated} dayTotals={currentDay.totals} />

                  <Card className="overflow-hidden">
                    {viewMode === 'gantt' ? (
                      <>
                        <TimeAxis />
                        {currentDay.labs.length === 0 && (currentDay.unallocated?.batches.length || 0) === 0 ? (
                          <div className="p-8 text-center text-xs text-slate-400 italic">
                            No labs match the current filters on {daysAgoOrIn(d.date, now)}.
                          </div>
                        ) : (
                          <>
                            {currentDay.labs.map((lab) => (
                              <LabTimeline key={lab.lab_id} lab={lab} batches={lab.batches} />
                            ))}
                            {(currentDay.unallocated?.batches?.length || 0) > 0 && (
                              <LabTimeline
                                lab={{
                                  lab_id: null,
                                  lab_number: 'Unallocated',
                                  location_name: `${currentDay.unallocated.batches.length} batches`,
                                }}
                                batches={currentDay.unallocated.batches}
                                isUnallocated
                              />
                            )}
                          </>
                        )}
                      </>
                    ) : (
                      <LabGridView day={currentDay} unallocated={currentDay.unallocated} />
                    )}
                  </Card>

                  {/* Clash detail (if any) */}
                  {currentDay.clashes && currentDay.clashes.length > 0 && (
                    <Card className="p-3 mt-3 border-rose-200 bg-rose-50/50" data-testid="lab-clashes">
                      <p className="text-[11px] font-semibold text-rose-800 mb-1.5 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> Physical lab clashes ({currentDay.clashes.length})
                      </p>
                      <div className="space-y-1">
                        {currentDay.clashes.map((cl, i) => {
                          const s = `${String(Math.floor(cl.start_min / 60)).padStart(2, '0')}:${String(cl.start_min % 60).padStart(2, '0')}`;
                          const e = `${String(Math.floor(cl.end_min / 60)).padStart(2, '0')}:${String(cl.end_min % 60).padStart(2, '0')}`;
                          return (
                            <div key={i} className="text-[11px] text-rose-700">
                              <strong>{cl.lab_number} · {cl.location_name}</strong> · overlap {fmt12h(s)} → {fmt12h(e)} · {cl.batch_ids.length} batches sharing the space
                            </div>
                          );
                        })}
                      </div>
                    </Card>
                  )}

                  <UntimedList day={currentDay} />
                </>
              )}
            </TabsContent>
          ))}
        </Tabs>
      )}

      {loading && <p className="text-center text-slate-400 text-xs py-8">Loading trainer lab timeline…</p>}
    </div>
  );
}
